import { useState } from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Sparkles, ShieldQuestion, Cog, ScrollText, Lightbulb, Feather, Infinity as InfinityIcon, Hash, Target, Waypoints, Quote } from "lucide-react";
import { Capsule } from "@/components/Capsule";
import { CinematicVideo } from "@/components/CinematicVideo";

const CAPSULES_VIDEO = "https://customer-assets-7cd3h4nn.emergentagent.net/job_epiphany-mentor/artifacts/lp6ap55n_gemini_generated_video_f67598c9.mp4";

const CAPSULES = [
  { kind: "myth", icon: ShieldQuestion, prefix: "myth", testId: "capsule-myth" },
  { kind: "how", icon: Cog, prefix: "how", testId: "capsule-how" },
  { kind: "origin", icon: ScrollText, prefix: "origin", testId: "capsule-origin" },
  { kind: "astuce", icon: Lightbulb, prefix: "astuce", testId: "capsule-astuce" },
  { kind: "mot", icon: Feather, prefix: "mot", testId: "capsule-mot" },
  { kind: "paradoxe", icon: InfinityIcon, prefix: "paradoxe", testId: "capsule-paradoxe" },
  { kind: "chiffre", icon: Hash, prefix: "chiffre", testId: "capsule-chiffre" },
  { kind: "devine", icon: Target, prefix: "devine", testId: "capsule-devine" },
  { kind: "connexion", icon: Waypoints, prefix: "connexion", testId: "capsule-connexion" },
  { kind: "citation", icon: Quote, prefix: "citation", testId: "capsule-citation" },
];

export function CapsulesHub() {
  const { t } = useTranslation();
  const [active, setActive] = useState(() => Math.floor(Math.random() * CAPSULES.length));
  const current = CAPSULES[active];

  return (
    <section className="relative overflow-hidden" data-testid="capsules-hub-section">
      <div className="amber-glow" aria-hidden style={{ top: "6%", left: "50%", transform: "translateX(-50%)", width: "780px", height: "540px", opacity: 0.85 }} />
      <div className="max-w-4xl mx-auto px-6 py-16 sm:py-20 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9 }}
          className="text-center mb-9"
        >
          <p className="inline-flex items-center gap-2 text-primary/80 tracking-[0.25em] uppercase text-xs mb-5">
            <Sparkles size={14} /> {t("landing.capsulesHub.eyebrow")}
          </p>
          <h2 className="font-display text-gradient-orange text-4xl sm:text-5xl leading-[1.08] tracking-tight mb-4" data-testid="capsules-hub-title">
            {t("landing.capsulesHub.title")}
          </h2>
          <p className="text-foreground/55 italic max-w-md mx-auto">{t("landing.capsulesHub.subtitle")}</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9 }}
          className="max-w-2xl mx-auto mb-10"
        >
          <CinematicVideo fade noSound silver src={CAPSULES_VIDEO} testId="capsules-cinematic-video" caption={t("landing.capsulesHub.title")} />
        </motion.div>

        <div className="grid grid-cols-2 md:grid-cols-5 gap-2.5 sm:gap-3 mb-9" role="tablist">
          {CAPSULES.map((c, i) => {
            const Icon = c.icon;
            const on = active === i;
            return (
              <motion.button
                key={c.kind}
                onClick={() => setActive(i)}
                data-testid={`capsule-chip-${c.kind}`}
                initial={{ opacity: 0, y: 14 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.04 }}
                whileHover={{ y: -4 }}
                whileTap={{ scale: 0.96 }}
                aria-selected={on}
                role="tab"
                className={`group card-sheen relative flex flex-col items-center justify-center gap-2.5 h-24 sm:h-28 rounded-2xl border px-2.5 text-center overflow-hidden transition-colors duration-300 ${
                  on
                    ? "border-primary/50 bg-primary/[0.08] text-primary shadow-[0_12px_44px_-14px_hsl(28_96%_56%_/_0.6)]"
                    : "border-white/10 bg-white/[0.02] text-foreground/60 hover:border-primary/30 hover:text-primary/90"
                }`}
              >
                {on && (
                  <span
                    aria-hidden
                    className="pointer-events-none absolute inset-0"
                    style={{ background: "radial-gradient(circle at 50% 28%, hsl(28 96% 56% / 0.18), transparent 68%)" }}
                  />
                )}
                <span
                  className={`relative z-10 flex items-center justify-center w-10 h-10 rounded-full border transition-colors duration-300 ${
                    on ? "border-primary/50 bg-primary/15" : "border-white/10 bg-white/[0.03] group-hover:border-primary/30"
                  }`}
                >
                  <Icon size={18} strokeWidth={1.7} className={on ? "pulse-star" : ""} />
                </span>
                <span className="relative z-10 text-[11px] sm:text-xs font-medium leading-tight line-clamp-2">
                  {t(`atelier.${c.prefix}Title`)}
                </span>
              </motion.button>
            );
          })}
        </div>

        <Capsule key={current.kind} kind={current.kind} icon={current.icon} prefix={current.prefix} testId={current.testId} />
      </div>
    </section>
  );
}
